import React from "react";
import { SpinningLoader } from "risa-oasis-ui_v2";
import { CommonLiterals } from "../../../../enums/StringLiterals/commonLiterals";
import { InfoIcon } from "../../../../svg/info-icon";
import {
  AlternativeDrugsSection,
  ApprovedSummaryContent,
  KeyPhrasesCard,
  NotesCard,
  ProviderSummaryCard,
  RecommendedActionsSection,
  StatusClassificationRow,
} from "./caseSummary";
import { parseListField } from "./caseSummary/caseSummaryUtils";

export interface CaseSummaryTabProps {
  rowData?: any;
}

/**
 * Extracts the response status text from the table row data.
 * The table row stores it as responseStpStatus.text (transformed) or response_status (raw).
 */
const getResponseStatus = (rowData: any): string => {
  return rowData?.responseStpStatus?.text || rowData?.response_status || "";
};

/**
 * Returns true when the row data contains any fields used by the Case Summary tab.
 *
 * @param rowData - Selected table row data for the order
 * @returns Whether the Case Summary tab has any content to display
 */
export const hasCaseSummaryData = (rowData: any): boolean => {
  return Boolean(
    rowData?.pa_outcome_classification ||
    rowData?.pa_outcome_provider_summary ||
    rowData?.pa_outcome_notes ||
    rowData?.pa_outcome_key_phrases ||
    rowData?.pa_outcome_recommended_actions ||
    rowData?.pa_outcome_alternative_drugs ||
    rowData?.pa_outcome_auth_expiration,
  );
};

/**
 * Tab component that displays the PA outcome case summary for an order.
 * Renders an approved layout (status + auth expiry) when response_status is "Approved",
 * and a denied layout (full detail card) otherwise.
 */
const CaseSummaryTab: React.FC<CaseSummaryTabProps> = ({ rowData }) => {
  const loading = false;
  const responseStatus = getResponseStatus(rowData);
  const isApproved = responseStatus.toLowerCase() === "approved";

  const hasData = hasCaseSummaryData(rowData);

  const keyPhrases = parseListField(rowData?.pa_outcome_key_phrases);
  const recommendedActions = parseListField(
    rowData?.pa_outcome_recommended_actions,
  );
  const alternativeDrugs = parseListField(
    rowData?.pa_outcome_alternative_drugs,
  );

  return (
    <div className="flex h-full w-full border-1 border-primaryGray-15 bg-[#F7F9FA]">
      <div className="flex h-full flex-1 flex-col overflow-hidden rounded-md border border-primaryGray-15 bg-white">
        <div className="flex flex-row justify-between border-b border-primaryGray-15 bg-primaryGray-16 px-4 py-2 text-small font-semibold leading-6 text-primaryGray-1 shadow-md">
          Case Summary
        </div>
        <div className="flex flex-1 flex-col overflow-auto">
          {loading ? (
            <div className="flex h-full w-full flex-col items-center justify-center gap-2">
              <SpinningLoader />
              <div className="text-sm text-primaryGray-6">
                {CommonLiterals.LOADING}
              </div>
            </div>
          ) : hasData ? (
            isApproved ? (
              <ApprovedSummaryContent
                responseStatus={responseStatus}
                authExpiryDate={rowData?.pa_outcome_auth_expiration}
              />
            ) : (
              <DeniedSummaryContent
                responseStatus={responseStatus}
                rowData={rowData}
                keyPhrases={keyPhrases}
                recommendedActions={recommendedActions}
                alternativeDrugs={alternativeDrugs}
              />
            )
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center gap-2">
              <InfoIcon
                width="48"
                height="48"
                stroke="#6B7280"
                strokeWidth="1"
              />
              <div className="text-sm text-primaryGray-6">
                No case summary available
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface DeniedSummaryContentProps {
  responseStatus: string;
  rowData?: any;
  keyPhrases: string[];
  recommendedActions: string[];
  alternativeDrugs: string[];
}

/**
 * Renders the denied/non-approved case summary with full detail sections.
 */
const DeniedSummaryContent: React.FC<DeniedSummaryContentProps> = ({
  responseStatus,
  rowData,
  keyPhrases,
  recommendedActions,
  alternativeDrugs,
}) => (
  <div className="flex flex-col rounded-[14px] border border-[#E6E6E6] shadow-sm">
    <div className="flex flex-col gap-4 border-b border-[#E6E6E6] bg-[rgba(247,249,250,0.5)] px-6 pb-6 pt-6">
      <StatusClassificationRow
        responseStatus={responseStatus}
        classification={rowData?.pa_outcome_classification}
        confidence={rowData?.pa_outcome_confidence}
      />
      {rowData?.pa_outcome_provider_summary && (
        <ProviderSummaryCard summary={rowData.pa_outcome_provider_summary} />
      )}
      {rowData?.pa_outcome_notes && (
        <NotesCard notes={rowData.pa_outcome_notes} />
      )}
      {keyPhrases.length > 0 && <KeyPhrasesCard phrases={keyPhrases} />}
    </div>

    <div className="flex flex-col gap-6 px-6 py-6">
      {recommendedActions.length > 0 && (
        <RecommendedActionsSection actions={recommendedActions} />
      )}
      {alternativeDrugs.length > 0 && (
        <AlternativeDrugsSection drugs={alternativeDrugs} />
      )}
    </div>
  </div>
);

export default CaseSummaryTab;
